import i18n, { type ParseKeys } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import de from '@/locales/de/common.json';
import en from '@/locales/en/common.json';

export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** A key of the English locale file. */
export type TranslationKey = ParseKeys<'common'>;

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: { common: typeof en };
  }
}

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
};

/** Returns true when the app ships the language. */
export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
}

/** Returns a language resolved to one this app ships. It defaults to the active language. */
export function activeLanguage(language: string = i18n.language): SupportedLanguage {
  const [base = ''] = (language ?? '').split('-');
  return isSupportedLanguage(base) ? base : 'en';
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: en },
      de: { common: de },
    },
    defaultNS: 'common',
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES,
    interpolation: { escapeValue: false },
  });

/** Writes the active language to `<html lang>`, if there is a document. */
function syncDocumentLanguage(): void {
  if (typeof document !== 'undefined') document.documentElement.lang = activeLanguage();
}

i18n.on('languageChanged', syncDocumentLanguage);

syncDocumentLanguage();

export { i18n };
